import { type DragEvent, type ReactNode } from "react";
import { GripVertical } from "lucide-react";

import { cn } from "@/lib/utils";

export type SortDirection = "asc" | "desc";

export type SortableTableHeaderColumnReorder = {
  onDragStart: (event: DragEvent<HTMLButtonElement>) => void;
  onDragOver: (event: DragEvent<HTMLTableCellElement>) => void;
  onDrop: (event: DragEvent<HTMLTableCellElement>) => void;
  onDragEnd: () => void;
  isDragOver: boolean;
};

export type SortableTableHeaderProps<T extends string = string> = {
  label: ReactNode;
  sortKey: T;
  activeKey: T;
  dir: SortDirection;
  onSort: (key: T) => void;
  align?: "left" | "right";
  className?: string;
  columnReorder?: SortableTableHeaderColumnReorder;
};

/** Default sortable `<th>` for hub data tables — see docs/ui-style-guide.md § Tables and data lists. */
export function SortableTableHeader<T extends string = string>({
  label,
  sortKey,
  activeKey,
  dir,
  onSort,
  align = "left",
  className,
  columnReorder,
}: SortableTableHeaderProps<T>) {
  const active = activeKey === sortKey;
  return (
    <th
      scope="col"
      className={cn(
        "select-none bg-slate-50 px-2 py-2 text-xs font-medium uppercase tracking-wide text-slate-600",
        columnReorder == null ? "cursor-pointer hover:bg-slate-100" : columnReorder.isDragOver ? "bg-brand-50" : "hover:bg-slate-100",
        align === "right" ? "text-right" : "text-left",
        className,
      )}
      onDragOver={columnReorder?.onDragOver}
      onDrop={columnReorder?.onDrop}
    >
      <div className={cn("flex items-center gap-1", align === "right" && "justify-end")}>
        {columnReorder != null ? (
          <button
            type="button"
            draggable
            className="cursor-grab rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600 active:cursor-grabbing"
            aria-label="Drag to reorder column"
            onClick={(event) => event.stopPropagation()}
            onDragStart={columnReorder.onDragStart}
            onDragEnd={columnReorder.onDragEnd}
          >
            <GripVertical className="h-3.5 w-3.5" aria-hidden />
          </button>
        ) : null}
        <button
          type="button"
          className={cn(
            "min-w-0 flex-1 text-left uppercase tracking-wide",
            align === "right" && "text-right",
          )}
          onClick={() => onSort(sortKey)}
        >
          {label}
          {active ? (dir === "asc" ? " ▲" : " ▼") : ""}
        </button>
      </div>
    </th>
  );
}
